import { useCallback } from 'react';

import { useAppContext } from 'app/context/app';
import { getProviderDetail } from 'app/services/gakService';
import { SelectActivityProviderStatus } from 'app/utils/constants';
import { FetchProviderResponse, FetchProvidersResponse } from 'app/interfaces/api';

export default function useActivityProvider() {
  const {
    actions,
    currentState: { providerDetails, availableProviders }
  } = useAppContext();

  /**
   * Updates provider detail state to match the specified provider ID
   */
  const selectActivityProvider = useCallback(
    async (providerId: string) => {
      if (providerDetails?.id === providerId) {
        // No change in selected provider, do nothing.
        return;
      }

      const providerDetail = await getProviderDetail(providerId);
      actions.setProviderDetails(providerDetail);
      if (providerDetail) return SelectActivityProviderStatus.Success;
      return SelectActivityProviderStatus.Failed;
    },
    [actions, providerDetails?.id]
  );

  /**
   * Refreshes the currently selected activity provider's information.
   */
  const refreshActivityProvider = useCallback(async () => {
    if (!providerDetails?.id) {
      throw new Error('Attempted to refresh activity provider when none was set');
    }

    const providerDetail = await getProviderDetail(providerDetails.id);
    actions.setProviderDetails(providerDetail);

    if (availableProviders && availableProviders?.length > 0) {
      const editedAvailableProviders: FetchProvidersResponse = (availableProviders || []).map(
        element => {
          if (element.providerId === providerDetails?.id) {
            const itemToReplace: FetchProviderResponse = {
              orgName: providerDetail.organisation.name,
              providerId: providerDetails?.id
            };
            return itemToReplace;
          }
          return element;
        }
      );

      actions.setAvailableProviders(editedAvailableProviders);
    }
  }, [actions, providerDetails?.id, availableProviders]);

  return {
    providerDetails,
    availableProviders,
    selectActivityProvider,
    refreshActivityProvider
  };
}
